import React, { useEffect, useState } from "react";
import {
  TextField,
  IconButton,
  Typography,
  Card,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
} from "@mui/material";
import { Formik, Form } from "formik";
import * as yup from "yup";
import { ChevronDown, Plus, MinusSquare, Calendar, Video } from "react-feather";
import { v4 as uuidv4 } from "uuid";
import moment from "moment/moment";
import CustomButton from "../../../components/button";
import AddVideoDialog from "../../../components/modal/videodialog";
import AlertModal from "../../../components/modal";
import Switch from "@mui/material/Switch";
import { styled } from "@mui/material/styles";

const validationSchema = yup.object().shape({
  name: yup.string().required("Numele campaniei este necesar"),
  client: yup.string().required("Numele clientului este necesar"),
});

const styles = {
  select: {
    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: "#f9f9f9",
    },
    "& .MuiInputLabel-root": { color: "#555" },
    "&.Mui-disabled .MuiOutlinedInput-root": { backgroundColor: "#f0f0f0" },
  },
  container: {
    display: "flex",
    gap: "40px",
    width: "100%",
    height: "100%",
  },
  leftSection: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  rightSection: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
};

export default function AddCampaignStep({
  data = {},
  onChange = () => {},
  goNext = () => {},
  handleStatus = () => {},
  formikRef,
}) {
  const {
    name = "",
    client = "",
    observations = "",
    schedules: initialSchedules = [],
  } = data;

  const [schedules, setSchedules] = useState(initialSchedules);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [currentScheduleIndex, setCurrentScheduleIndex] = useState(null);
  const [toastStatus, setToastStatus] = useState(false);
  const [toastDescription, setToastDescription] = useState("");
  const [alertDeleteSchedule, setAlertDeleteSchedule] = useState(false);
  const [scheduleToRemove, setScheduleToRemove] = useState(null);
  const [expandedAccordion, setExpandedAccordion] = useState(null);

  useEffect(() => {
    onChange({
      name,
      client,
      observations,
      schedules,
    });
  }, [name, client, observations, schedules, onChange]);

  const handleFormikSubmit = (values) => {
    if (!schedules || schedules.length === 0) {
      handleStatus(
        "Adauga cel putin o perioada cu spoturi de difuzare inainte de a continua.",
        setToastStatus,
        setToastDescription
      );
      return;
    }

    // Check for duplicate periods (same start and end dates)
    const duplicatePeriods = [];
    for (let i = 0; i < schedules.length; i++) {
      for (let j = i + 1; j < schedules.length; j++) {
        if (
          schedules[i].startDate === schedules[j].startDate &&
          schedules[i].endDate === schedules[j].endDate
        ) {
          duplicatePeriods.push({
            period1: i + 1,
            period2: j + 1,
            startDate: schedules[i].startDate,
            endDate: schedules[i].endDate,
          });
        }
      }
    }

    if (duplicatePeriods.length > 0) {
      const duplicateMessages = duplicatePeriods.map(
        (dup) =>
          `Perioada ${dup.period1} si Perioada ${dup.period2} (${moment(
            dup.startDate
          ).format("DD.MM.YYYY")} - ${moment(dup.endDate).format(
            "DD.MM.YYYY"
          )})`
      );
      handleStatus(
        `Urmatoarele perioade au date identice si trebuie sa fie diferite: ${duplicateMessages.join(
          ", "
        )}`,
        setToastStatus,
        setToastDescription
      );
      return;
    }

    // Check for periods without videos
    const periodsWithoutVideos = schedules
      .map((schedule, index) => ({ schedule, index }))
      .filter(
        ({ schedule }) => !schedule.videos || schedule.videos.length === 0
      )
      .map(({ index }) => index + 1);

    if (periodsWithoutVideos.length > 0) {
      const periodText =
        periodsWithoutVideos.length === 1
          ? `Perioada ${periodsWithoutVideos[0]} nu are`
          : `Perioadele ${periodsWithoutVideos.join(", ")} nu au`;

      handleStatus(
        `${periodText} niciun spot adaugat. Adauga cel putin un spot pentru fiecare perioada.`,
        setToastStatus,
        setToastDescription
      );
      return;
    }

    onChange({
      name: values.name,
      client: values.client,
      observations: values.observations,
      schedules,
    });
    goNext();
  };

  const handleAddSchedule = () => {
    const newSchedule = {
      id: uuidv4(),
      startDate: moment().format("YYYY-MM-DD"),
      endDate: moment().add(1, "month").format("YYYY-MM-DD"),
      videos: [],
      videoFrequency: [],
    };
    const newSchedules = [...schedules, newSchedule];
    setSchedules(newSchedules);
    // Open the newly added accordion (last one)
    setExpandedAccordion(newSchedules.length - 1);
  };

  const handleAccordionChange = (index) => (event, isExpanded) => {
    // Only one accordion can be expanded at a time
    setExpandedAccordion(isExpanded ? index : null);
  };

  const handleDeleteSchedule = (index) => {
    setScheduleToRemove(index);
    setAlertDeleteSchedule(true);
  };

  const executeDeleteSchedule = () => {
    if (scheduleToRemove !== null) {
      setSchedules(schedules.filter((_, i) => i !== scheduleToRemove));
      // If we're deleting the expanded accordion, close it
      if (expandedAccordion === scheduleToRemove) {
        setExpandedAccordion(null);
      } else if (expandedAccordion > scheduleToRemove) {
        // If we're deleting an accordion before the expanded one, adjust the index
        setExpandedAccordion(expandedAccordion - 1);
      }
      setAlertDeleteSchedule(false);
      setScheduleToRemove(null);
    }
  };

  const validateDateInput = (value, field, schedule) => {
    // Only validate if the date is in a complete format (YYYY-MM-DD)
    if (!value || !value.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return null; // Don't validate incomplete dates
    }

    const selectedDate = moment(value);
    const today = moment().startOf("day");

    // Check if the date is valid
    if (!selectedDate.isValid()) {
      return `Data introdusa nu este valida.`;
    }

    // Don't allow dates in the past
    if (selectedDate.isBefore(today)) {
      return `Data ${
        field === "startDate" ? "de inceput" : "de sfarsit"
      } nu poate fi in trecut.`;
    }

    // Don't allow invalid years (beyond reasonable range)
    const year = selectedDate.year();
    if (year < 2024 || year > 2030) {
      return `Anul selectat (${year}) nu este valid.`;
    }

    // Check relationship between start and end dates
    if (field === "endDate" && schedule.startDate) {
      const startDate = moment(schedule.startDate);
      if (startDate.isValid() && selectedDate.isBefore(startDate)) {
        return "Data de sfarsit nu poate fi inainte de data de inceput.";
      }
    }

    if (field === "startDate" && schedule.endDate) {
      const endDate = moment(schedule.endDate);
      if (endDate.isValid() && selectedDate.isAfter(endDate)) {
        return "Data de inceput nu poate fi dupa data de sfarsit.";
      }
    }

    return null; // No validation errors
  };

  const handleScheduleChange = (index, field, value) => {
    // Always update the schedule first to allow typing
    const updatedSchedules = [...schedules];
    updatedSchedules[index] = {
      ...updatedSchedules[index],
      [field]: value,
    };
    setSchedules(updatedSchedules);
  };

  const handleScheduleBlur = (index, field, value) => {
    const schedule = schedules[index];
    const validationError = validateDateInput(value, field, schedule);

    if (validationError) {
      handleStatus(validationError, setToastStatus, setToastDescription);
    }
  };

  const handleOpenVideoModal = (scheduleIndex) => {
    setCurrentScheduleIndex(scheduleIndex);
    setIsVideoModalOpen(true);
  };

  const handleVideoModalClose = () => {
    setIsVideoModalOpen(false);
    setCurrentScheduleIndex(null);
  };

  const handleSubmitVideo = (videoArray) => {
    if (currentScheduleIndex !== null) {
      const updatedSchedules = [...schedules];
      updatedSchedules[currentScheduleIndex].videos = [
        ...updatedSchedules[currentScheduleIndex].videos,
        ...videoArray,
      ];
      // Initialize frequency for new videos
      const newFrequencies = videoArray.map(() => 1);
      updatedSchedules[currentScheduleIndex].videoFrequency = [
        ...updatedSchedules[currentScheduleIndex].videoFrequency,
        ...newFrequencies,
      ];
      setSchedules(updatedSchedules);
    }
    handleVideoModalClose();
  };

  const handleRemoveVideo = (scheduleIndex, videoIndex) => {
    const updatedSchedules = [...schedules];
    updatedSchedules[scheduleIndex].videos = updatedSchedules[
      scheduleIndex
    ].videos.filter((_, i) => i !== videoIndex);
    updatedSchedules[scheduleIndex].videoFrequency = updatedSchedules[
      scheduleIndex
    ].videoFrequency.filter((_, i) => i !== videoIndex);
    setSchedules(updatedSchedules);
  };

  const handleVideoFrequencyChange = (scheduleIndex, videoIndex, frequency) => {
    const updatedSchedules = [...schedules];
    updatedSchedules[scheduleIndex].videoFrequency[videoIndex] = frequency;
    setSchedules(updatedSchedules);
  };

  return (
    <>
      <Formik
        innerRef={formikRef}
        initialValues={{
          name: name,
          client: client,
          observations: observations,
        }}
        validationSchema={validationSchema}
        onSubmit={handleFormikSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, handleChange, handleBlur }) => (
          <Form style={styles.container}>
            <div style={styles.leftSection}>
              <TextField
                name="name"
                label="Nume campanie"
                variant="outlined"
                fullWidth
                sx={styles.select}
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.name && Boolean(errors.name)}
                helperText={touched.name && errors.name}
              />

              <TextField
                name="client"
                label="Nume client"
                variant="outlined"
                fullWidth
                sx={styles.select}
                value={values.client}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.client && Boolean(errors.client)}
                helperText={touched.client && errors.client}
              />

              <TextField
                name="observations"
                label="Observatii"
                variant="outlined"
                fullWidth
                multiline
                rows={4}
                sx={styles.select}
                value={values.observations}
                onChange={handleChange}
                onBlur={handleBlur}
              />

              <div
                style={{
                  padding: "20px",
                  background: "linear-gradient(135deg, #f8f9fa, #ffffff)",
                  borderRadius: "16px",
                  border: "1px solid rgba(226,232,240,0.8)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    marginBottom: "16px",
                  }}
                >
                  <div
                    style={{
                      width: "24px",
                      height: "24px",
                      borderRadius: "50%",
                      background:
                        values.name && values.client ? "#22c55e" : "#e5e7eb",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      fontFamily: "SeagoeUIBold",
                      fontSize: "14px",
                    }}
                  >
                    {values.name && values.client ? "✓" : "1"}
                  </div>
                  <span
                    style={{
                      fontSize: "14px",
                      fontFamily: "SeagoeUI",
                      color:
                        values.name && values.client ? "#22c55e" : "#64748b",
                      fontFamily:
                        values.name && values.client
                          ? "SeagoeUIBold"
                          : "SeagoeUI",
                    }}
                  >
                    Informatii campanie
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  <div
                    style={{
                      width: "24px",
                      height: "24px",
                      borderRadius: "50%",
                      background:
                        schedules.length > 0 &&
                        schedules.every((s) => s.videos?.length > 0)
                          ? "#22c55e"
                          : "#e5e7eb",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      fontSize: "14px",
                      fontFamily: "SeagoeUIBold",
                    }}
                  >
                    {schedules.length > 0 &&
                    schedules.every((s) => s.videos?.length > 0)
                      ? "✓"
                      : "2"}
                  </div>
                  <span
                    style={{
                      fontSize: "14px",
                      fontFamily: "SeagoeUI",
                      color:
                        schedules.length > 0 &&
                        schedules.every((s) => s.videos?.length > 0)
                          ? "#22c55e"
                          : "#64748b",
                      fontFamily:
                        schedules.length > 0 &&
                        schedules.every((s) => s.videos?.length > 0)
                          ? "SeagoeUIBold"
                          : "SeagoeUI",
                    }}
                  >
                    Perioade si spoturi
                  </span>
                </div>
              </div>
            </div>

            <div style={styles.rightSection}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "24px",
                  position: "relative",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                    position: "relative",
                    zIndex: 2,
                  }}
                >
                  <span
                    style={{
                      fontSize: "14px",
                      fontFamily: "SeagoeUI",
                      color: "#ff4757",
                      textTransform: "uppercase",
                      letterSpacing: "1.5px",
                    }}
                  >
                    PERIOADE CAMPANIE
                  </span>
                  <h2
                    style={{
                      fontSize: "2.5rem",
                      fontFamily: "SeagoeUIBold",
                      color: "#2c3e50",
                      margin: 0,
                      position: "relative",
                    }}
                  >
                    Perioade & Spoturi
                    <div
                      style={{
                        position: "absolute",
                        bottom: "-4px",
                        left: 0,
                        width: "40%",
                        height: "4px",
                        background: "#ff4757",
                        borderRadius: "2px",
                      }}
                    />
                  </h2>
                </div>
                <div
                  style={{
                    height: "2px",
                    flex: 1,
                    marginLeft: "24px",
                    background:
                      "linear-gradient(to right, rgba(255, 71, 87, 0.5) 0%, transparent 100%)",
                  }}
                />
              </div>

              <div
                style={{
                  padding: "16px",
                  background: "rgba(34, 197, 94, 0.05)",
                  borderRadius: "12px",
                  border: "1px solid rgba(34, 197, 94, 0.2)",
                }}
              >
                <div
                  style={{
                    fontSize: "14px",
                    color: "#16a34a",
                    fontFamily: "SeagoeUI",
                    lineHeight: "1.5",
                  }}
                >
                  <label style={{ fontFamily: "SeagoeUIBold" }}>
                    Cum functioneaza:
                  </label>
                  <br />
                  1. Adauga una sau mai multe perioade pentru campania ta
                  <br />
                  2. Pentru fiecare perioada, adauga spoturile care vor fi
                  difuzate in acea perioada
                  <br />
                  3. Seteaza frecventa pe ora pentru fiecare spot de difuzare
                  <br />
                  4. Continua la selectia playerelor pentru a finaliza campania
                </div>
              </div>

              <CustomButton
                text="Adauga Perioada"
                onClick={handleAddSchedule}
                style={{
                  background: "linear-gradient(135deg, #ff4757, #ff6b7a)",
                }}
              />

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                {schedules.map((schedule, scheduleIndex) => (
                  <Card
                    key={schedule.id}
                    style={{
                      borderRadius: "16px",
                      boxShadow: "0 4px 20px rgba(148,163,184,0.1)",
                      border: "1px solid rgba(226,232,240,0.8)",
                    }}
                  >
                    <Accordion
                      expanded={expandedAccordion === scheduleIndex}
                      onChange={handleAccordionChange(scheduleIndex)}
                    >
                      <AccordionSummary
                        expandIcon={<ChevronDown />}
                        style={{
                          background:
                            "linear-gradient(135deg, #f8f9fa, #ffffff)",
                          borderRadius: "16px 16px 0 0",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            width: "100%",
                          }}
                        >
                          <Calendar size={20} color="#ff4757" />
                          <Typography
                            variant="h6"
                            style={{
                              fontFamily: "SeagoeUIBold",
                              color: "#2c3e50",
                              flex: 1,
                            }}
                          >
                            Spoturi Perioada {scheduleIndex + 1}
                          </Typography>
                          <Typography
                            variant="body2"
                            style={{
                              color: "#64748b",
                              marginRight: "8px",
                            }}
                          >
                            {moment(schedule.startDate).format("DD.MM.YYYY")} →{" "}
                            {moment(schedule.endDate).format("DD.MM.YYYY")}
                          </Typography>
                          <Typography
                            variant="body2"
                            style={{
                              color: "#64748b",
                              marginRight: "8px",
                            }}
                          >
                            {schedule.videos?.length || 0} spoturi
                          </Typography>
                          <Tooltip title="Sterge perioada">
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteSchedule(scheduleIndex);
                              }}
                              size="medium"
                              style={{
                                color: "#ef4444",
                              }}
                            >
                              <MinusSquare size={22} />
                            </IconButton>
                          </Tooltip>
                        </div>
                      </AccordionSummary>
                      <AccordionDetails>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "16px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              gap: "16px",
                            }}
                          >
                            <TextField
                              type="date"
                              label="Data inceput"
                              value={schedule.startDate}
                              onChange={(e) =>
                                handleScheduleChange(
                                  scheduleIndex,
                                  "startDate",
                                  e.target.value
                                )
                              }
                              onBlur={(e) =>
                                handleScheduleBlur(
                                  scheduleIndex,
                                  "startDate",
                                  e.target.value
                                )
                              }
                              sx={{
                                ...styles.select,
                                flex: 1,
                              }}
                              inputProps={{
                                min: moment().format("YYYY-MM-DD"),
                                max: "2030-12-31",
                              }}
                              helperText="Data nu poate fi in trecut"
                            />
                            <TextField
                              type="date"
                              label="Data sfarsit"
                              value={schedule.endDate}
                              onChange={(e) =>
                                handleScheduleChange(
                                  scheduleIndex,
                                  "endDate",
                                  e.target.value
                                )
                              }
                              onBlur={(e) =>
                                handleScheduleBlur(
                                  scheduleIndex,
                                  "endDate",
                                  e.target.value
                                )
                              }
                              sx={{
                                ...styles.select,
                                flex: 1,
                              }}
                              inputProps={{
                                min:
                                  schedule.startDate ||
                                  moment().format("YYYY-MM-DD"),
                                max: "2030-12-31",
                              }}
                              helperText="Data nu poate fi inainte de data de inceput"
                            />
                          </div>

                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                              marginTop: "16px",
                            }}
                          >
                            <Video size={20} color="#ff4757" />
                            <Typography
                              variant="h6"
                              style={{
                                fontFamily: "SeagoeUIBold",
                                color: "#2c3e50",
                                flex: 1,
                              }}
                            >
                              Spoturi pentru aceasta perioada de difuzare
                            </Typography>
                            <CustomButton
                              text="Adauga Spot"
                              onClick={() =>
                                handleOpenVideoModal(scheduleIndex)
                              }
                              style={{
                                padding: "8px 16px",
                                fontSize: "14px",
                              }}
                            />
                          </div>

                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "12px",
                            }}
                          >
                            {schedule.videos?.map((video, videoIndex) => (
                              <div
                                key={videoIndex}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "16px",
                                  padding: "12px 16px",
                                  background: "#f8f9fa",
                                  borderRadius: "12px",
                                  border: "1px solid #e9ecef",
                                }}
                              >
                                <div
                                  style={{
                                    width: "24px",
                                    height: "24px",
                                    borderRadius: "6px",
                                    background: "rgba(255,71,87,0.1)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "#ff4757",
                                    fontSize: "12px",
                                  }}
                                >
                                  {videoIndex + 1}
                                </div>
                                <div
                                  style={{
                                    flex: 1,
                                    fontSize: "14px",
                                    color: "#334155",
                                  }}
                                >
                                  {video.title}
                                </div>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: "12px",
                                      color: "#64748b",
                                    }}
                                  >
                                    Frecventa pe ora:
                                  </span>
                                  <TextField
                                    type="number"
                                    size="small"
                                    value={
                                      schedule.videoFrequency?.[videoIndex] || 1
                                    }
                                    onChange={(e) =>
                                      handleVideoFrequencyChange(
                                        scheduleIndex,
                                        videoIndex,
                                        parseInt(e.target.value) || 1
                                      )
                                    }
                                    inputProps={{
                                      min: 1,
                                      style: {
                                        width: "60px",
                                        textAlign: "center",
                                        padding: "4px",
                                      },
                                    }}
                                  />
                                </div>
                                <Tooltip title="Sterge spotul">
                                  <IconButton
                                    onClick={() =>
                                      handleRemoveVideo(
                                        scheduleIndex,
                                        videoIndex
                                      )
                                    }
                                    size="medium"
                                    style={{
                                      color: "#ef4444",
                                    }}
                                  >
                                    <MinusSquare size={20} />
                                  </IconButton>
                                </Tooltip>
                              </div>
                            ))}
                            {(!schedule.videos ||
                              schedule.videos.length === 0) && (
                              <div
                                style={{
                                  padding: "20px",
                                  textAlign: "center",
                                  color: "#64748b",
                                  fontSize: "14px",
                                  fontStyle: "italic",
                                  background: "#f8f9fa",
                                  borderRadius: "12px",
                                  border: "1px dashed #cbd5e1",
                                }}
                              >
                                Nu sunt spoturi adaugate pentru aceasta perioada
                              </div>
                            )}
                          </div>
                        </div>
                      </AccordionDetails>
                    </Accordion>
                  </Card>
                ))}
              </div>

              {schedules.length === 0 && (
                <div
                  style={{
                    padding: "40px",
                    textAlign: "center",
                    color: "#64748b",
                    fontSize: "16px",
                    background: "rgba(241,245,249,0.5)",
                    borderRadius: "16px",
                    border: "1px dashed rgba(148,163,184,0.4)",
                  }}
                >
                  Nu exista perioade adaugate inca
                </div>
              )}

              <div
                style={{
                  marginTop: "5px",
                  padding: "24px",
                  background: "linear-gradient(135deg, #f8f9fa, #ffffff)",
                  borderRadius: "16px",
                  border: "1px solid rgba(226,232,240,0.8)",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    marginBottom: "16px",
                    fontSize: "14px",
                    color: "#64748b",
                    fontFamily: "SeagoeUI",
                  }}
                >
                  {schedules.length === 0
                    ? "Adauga cel putin o perioada cu spoturi pentru a continua"
                    : schedules.some((s) => !s.videos || s.videos.length === 0)
                    ? "Adauga spoturi pentru toate perioadele pentru a continua"
                    : "Toate informatiile sunt complete! Poti trece la urmatorul pas."}
                </div>
                <button
                  type="submit"
                  className="add-video-button"
                  style={{
                    opacity:
                      schedules.length > 0 &&
                      schedules.every((s) => s.videos?.length > 0)
                        ? 1
                        : 0.5,
                    cursor:
                      schedules.length > 0 &&
                      schedules.every((s) => s.videos?.length > 0)
                        ? "pointer"
                        : "not-allowed",
                  }}
                >
                  Continua la Selectie Playere →
                </button>
              </div>
            </div>
          </Form>
        )}
      </Formik>

      <AddVideoDialog
        open={isVideoModalOpen}
        onClose={handleVideoModalClose}
        onSubmit={handleSubmitVideo}
        onSelect={handleSubmitVideo}
        instantUpload={false}
        isCampaign={true}
        canAddVideo={false}
      />

      <AlertModal
        open={alertDeleteSchedule}
        handleClose={() => {
          setAlertDeleteSchedule(false);
          setScheduleToRemove(null);
        }}
        text="Esti sigur ca vrei sa stergi aceasta perioada?"
        handleYes={executeDeleteSchedule}
      />
    </>
  );
}
